import { useState, useMemo } from 'react';
import { Placeholder, Spinner, IconButton } from '@telegram-apps/telegram-ui';
import { Villa } from '../types';
import VillaCard from './VillaCard';
import EditDrawer from './EditDrawer';
import StatusPicker from './StatusPicker';

interface VillasViewProps {
  villas: Villa[];
  loading: boolean;
  onRefresh: () => void;
  onChange: (villa: Villa) => void;
  onMoveToArchive: (id: string) => void;
}

// Simple icon components
const Icon28RefreshOutline = () => (
  <svg width={28} height={28} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 4V10M14 4C8.477 4 4 8.477 4 14C4 19.523 8.477 24 14 24C19.523 24 24 19.523 24 14M14 4L18 8M14 4L10 8"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Icon28ViewOutline = () => (
  <svg width={28} height={28} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="8" width="10" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
    <rect x="14" y="8" width="10" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

export default function VillasView({
  villas,
  loading,
  onRefresh,
  onChange,
  onMoveToArchive,
}: VillasViewProps) {
  const [viewMode, setViewMode] = useState<'list' | 'tiles'>('list');
  const [editingVilla, setEditingVilla] = useState<Villa | null>(null);
  const [statusPickerVilla, setStatusPickerVilla] = useState<Villa | null>(null);

  const pinnedVillas = useMemo(() => villas.filter(v => v.pinned), [villas]);
  const regularVillas = useMemo(() => villas.filter(v => !v.pinned), [villas]);

  const handleStatusChange = (villa: Villa, newStatus: string) => {
    const updated = { ...villa, status: newStatus as Villa['status'] };
    onChange(updated);
    setStatusPickerVilla(null);
  };

  if (loading) {
    return (
      <Placeholder>
        <Spinner size="l" />
        Загрузка...
      </Placeholder>
    );
  }

  if (villas.length === 0) {
    return (
      <Placeholder
        header="Нет вилл"
        description="Добавьте ссылки на виллы в Telegram-чат"
      >
        <IconButton onClick={onRefresh}>
          <Icon28RefreshOutline />
        </IconButton>
      </Placeholder>
    );
  }

  return (
    <>
      <div className="villa-view">
        <div className="villa-view__header">
          <h2>{villas.length} вилл</h2>
          <IconButton
            onClick={() => setViewMode(viewMode === 'list' ? 'tiles' : 'list')}
          >
            <Icon28ViewOutline />
          </IconButton>
        </div>

        {/* Pinned villas as stories */}
        {pinnedVillas.length > 0 && (
          <div className="pinned-stories">
            {pinnedVillas.map(villa => (
              <div
                key={villa.id}
                className="story-item"
                onClick={() => setEditingVilla(villa)}
              >
                <img src={villa.image || '/placeholder.svg'} alt="" />
              </div>
            ))}
          </div>
        )}

        {/* Regular villas */}
        <div className={`villa-list villa-list--${viewMode}`}>
          {regularVillas.map(villa => (
            <VillaCard
              key={villa.id}
              villa={villa}
              viewMode={viewMode}
              onStatusClick={(v) => setStatusPickerVilla(v)}
              onEdit={setEditingVilla}
            />
          ))}
        </div>
      </div>

      {/* Status Picker */}
      {statusPickerVilla && (
        <StatusPicker
          villa={statusPickerVilla}
          open={true}
          onClose={() => setStatusPickerVilla(null)}
          onChange={(newStatus) => handleStatusChange(statusPickerVilla, newStatus)}
        />
      )}

      {/* Edit Drawer */}
      {editingVilla && (
        <EditDrawer
          villa={editingVilla}
          open={true}
          onClose={() => setEditingVilla(null)}
          onChange={onChange}
          onMoveToArchive={onMoveToArchive}
        />
      )}
    </>
  );
}
